import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import Notification from './Notification'
import './ClientePDFs.css'

const ClientePDFs = ({ clienteId, clienteIdCadastro, clienteNome, onClose }) => {
  const [pdfs, setPdfs] = useState([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [uploadData, setUploadData] = useState({
    tipo_pdf: 'vistoria',
    arquivo: null,
    observacoes: ''
  })

  // Estados para notificações
  const [notification, setNotification] = useState(null)
  
  // Estados para modal de confirmação
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmAction, setConfirmAction] = useState(null)

  // Função para mostrar notificações
  const showNotification = (message, type = 'info', duration = 3000) => {
    setNotification({ message, type, duration })
  }

  // Carregar PDFs do cliente
  const loadPDFs = async () => {
    if (!clienteId && !clienteIdCadastro) return

    setLoading(true)
    try {
      const todosPDFs = []

      // 1. Buscar PDFs da tabela clientes_pdfs (referencia clientes.id)
      if (clienteId) {
        try {
          const { data: pdfsClientes, error: errorClientes } = await supabase
            .rpc('get_clientes_pdfs', { p_cliente_id: clienteId })

          if (!errorClientes && pdfsClientes) {
            // Adicionar origem para identificar de qual tabela veio
            const pdfsComOrigem = pdfsClientes.map(pdf => ({
              ...pdf,
              origem: 'clientes_pdfs',
              tipo_pdf: pdf.tipo_pdf || 'vistoria'
            }))
            todosPDFs.push(...pdfsComOrigem)
            console.log('📄 PDFs de clientes_pdfs carregados:', pdfsComOrigem.length)
          } else if (errorClientes) {
            console.warn('⚠️ Erro ao carregar PDFs de clientes_pdfs:', errorClientes)
          }
        } catch (error) {
          console.warn('⚠️ Erro ao buscar PDFs de clientes_pdfs:', error)
        }
      }

      // 2. Buscar PDFs da tabela documentos_clientes (referencia clientes_cadastro.id)
      if (clienteIdCadastro) {
        try {
          const { data: documentosClientes, error: errorDocumentos } = await supabase
            .from('documentos_clientes')
            .select('id, tipo_documento, nome_arquivo, caminho_arquivo, data_upload, observacao')
            .eq('cliente_id', clienteIdCadastro)
            .order('data_upload', { ascending: false })

          if (!errorDocumentos && documentosClientes) {
            // Converter documentos_clientes para o formato esperado
            // Mapear tipos de documento para tipos de PDF
            const tipoDocumentoParaPDF = {
              'vistoria': 'vistoria',
              'lista_material': 'lista_material',
              'analise_sombreamento': 'analise_sombreamento',
              'art_trt': 'projeto',
              'unifilar': 'projeto',
              'projeto': 'projeto'
            }

            const pdfsDocumentos = documentosClientes.map(doc => ({
              id: doc.id,
              tipo_pdf: tipoDocumentoParaPDF[doc.tipo_documento] || 'vistoria',
              nome_arquivo: doc.nome_arquivo || 'Documento',
              caminho_arquivo: doc.caminho_arquivo,
              tamanho_arquivo: 0, // Não temos essa informação em documentos_clientes
              data_upload: doc.data_upload,
              observacoes: doc.observacao || null,
              ativo: true,
              origem: 'documentos_clientes',
              tipo_documento_original: doc.tipo_documento
            }))
            todosPDFs.push(...pdfsDocumentos)
            console.log('📄 PDFs de documentos_clientes carregados:', pdfsDocumentos.length)
          } else if (errorDocumentos) {
            console.warn('⚠️ Erro ao carregar PDFs de documentos_clientes:', errorDocumentos)
          }
        } catch (error) {
          console.warn('⚠️ Erro ao buscar PDFs de documentos_clientes:', error)
        }
      }

      // Ordenar todos os PDFs por data de upload (mais recentes primeiro)
      todosPDFs.sort((a, b) => {
        const dateA = new Date(a.data_upload)
        const dateB = new Date(b.data_upload)
        return dateB - dateA
      })

      setPdfs(todosPDFs)
      console.log('✅ Total de PDFs carregados:', todosPDFs.length)
    } catch (error) {
      console.error('Erro ao carregar PDFs:', error)
      showNotification('Erro ao carregar PDFs: ' + error.message, 'error')
    } finally {
      setLoading(false)
    }
  }

  // Upload de PDF
  const handleUpload = async (e) => {
    e.preventDefault()
    
    if (!uploadData.arquivo) {
      showNotification('Selecione um arquivo PDF', 'warning')
      return
    }

    if (uploadData.arquivo.type !== 'application/pdf') {
      showNotification('Apenas arquivos PDF são permitidos', 'warning')
      return
    }

    setUploading(true)
    try {
      // Gerar nome único para o arquivo (clienteId ou clienteIdCadastro para o path)
      const timestamp = Date.now()
      const idPath = (clienteId || clienteIdCadastro || 'anon').toString()
      const fileName = `${idPath}_${uploadData.tipo_pdf}_${timestamp}.pdf`
      const filePath = `clientes-pdfs/${fileName}`

      // Upload para Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('documentos')
        .upload(filePath, uploadData.arquivo, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) throw uploadError

      // upload_cliente_pdf espera: p_cliente_id, p_tipo_pdf, p_nome_arquivo, p_caminho_arquivo, p_tamanho_arquivo, p_tipo_mime, p_observacoes (usa auth.uid() internamente)
      if (!clienteId) {
        showNotification('Upload disponível apenas ao abrir pelo cadastro do cliente (clientes.id).', 'warning')
        setUploading(false)
        return
      }
      const { error: dbError } = await supabase
        .rpc('upload_cliente_pdf', {
          p_cliente_id: clienteId,
          p_tipo_pdf: uploadData.tipo_pdf,
          p_nome_arquivo: uploadData.arquivo.name,
          p_caminho_arquivo: filePath,
          p_tamanho_arquivo: uploadData.arquivo.size,
          p_tipo_mime: uploadData.arquivo.type,
          p_observacoes: uploadData.observacoes || null
        })

      if (dbError) throw dbError

      // Recarregar lista de PDFs
      await loadPDFs()
      
      // Limpar formulário
      setUploadData({
        tipo_pdf: 'vistoria',
        arquivo: null,
        observacoes: ''
      })
      setShowUploadForm(false)
      
      showNotification('PDF enviado com sucesso!', 'success', 5000)
    } catch (error) {
      console.error('Erro ao fazer upload:', error)
      showNotification('Erro ao fazer upload: ' + error.message, 'error')
    } finally {
      setUploading(false)
    }
  }

  // Baixar PDF
  const handleDownload = async (pdf) => {
    try {
      // Se o PDF vem de documentos_clientes, o caminho_arquivo já é uma URL pública
      if (pdf.origem === 'documentos_clientes' && pdf.caminho_arquivo && pdf.caminho_arquivo.startsWith('http')) {
        // Abrir em nova aba ou fazer download direto
        const link = document.createElement('a')
        link.href = pdf.caminho_arquivo
        link.download = pdf.nome_arquivo || 'documento.pdf'
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        showNotification('PDF aberto para download', 'success')
        return
      }

      // Para PDFs de clientes_pdfs, fazer download do storage
      const { data, error } = await supabase.storage
        .from('documentos')
        .download(pdf.caminho_arquivo)

      if (error) throw error

      // Criar link para download
      const url = URL.createObjectURL(data)
      const link = document.createElement('a')
      link.href = url
      link.download = pdf.nome_arquivo
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      showNotification('PDF baixado com sucesso!', 'success')
    } catch (error) {
      console.error('Erro ao baixar PDF:', error)
      showNotification('Erro ao baixar PDF: ' + error.message, 'error')
    }
  }

  // Excluir PDF
  const handleDelete = (pdfId) => {
    setConfirmAction(() => () => executeDelete(pdfId))
    setShowConfirmModal(true)
  }

  // Executar exclusão após confirmação
  const executeDelete = async (pdfId) => {
    try {
      // Primeiro, buscar informações do PDF para obter o caminho do arquivo
      const { data: pdfData, error: fetchError } = await supabase
        .from('clientes_pdfs')
        .select('caminho_arquivo, nome_arquivo')
        .eq('id', pdfId)
        .single()

      if (fetchError) throw fetchError

      // Excluir o arquivo do bucket primeiro
      if (pdfData.caminho_arquivo) {
        const { error: storageError } = await supabase.storage
          .from('documentos')
          .remove([pdfData.caminho_arquivo])

        if (storageError) {
          console.warn('⚠️ Erro ao excluir arquivo do storage:', storageError)
          // Continuar mesmo se houver erro no storage
        } else {
          console.log('✅ Arquivo excluído do storage:', pdfData.nome_arquivo)
        }
      }

      // Depois excluir o registro da tabela
      const { error } = await supabase
        .rpc('delete_cliente_pdf', { p_pdf_id: pdfId })

      if (error) throw error

      await loadPDFs()
      showNotification('PDF excluído permanentemente!', 'success', 5000)
    } catch (error) {
      console.error('Erro ao excluir PDF:', error)
      showNotification('Erro ao excluir PDF: ' + error.message, 'error')
    }
  }

  // Confirmar ação
  const handleConfirm = () => {
    if (confirmAction) {
      confirmAction()
    }
    setShowConfirmModal(false)
    setConfirmAction(null)
  }

  // Cancelar ação
  const handleCancel = () => {
    setShowConfirmModal(false)
    setConfirmAction(null)
  }

  // Formatar tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Formatar data
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR')
  }

  // Obter ícone do tipo de PDF
  const getPDFIcon = (tipo) => {
    const icons = {
      vistoria: '🔍',
      projeto: '📐',
      lista_material: '📋',
      analise_sombreamento: '☀️'
    }
    return icons[tipo] || '📄'
  }

  // Obter nome do tipo de PDF
  const getPDFTypeName = (tipo) => {
    const names = {
      vistoria: 'Vistoria',
      projeto: 'Projeto',
      lista_material: 'Lista de Material',
      analise_sombreamento: 'Análise de Sombreamento'
    }
    return names[tipo] || tipo
  }

  useEffect(() => {
    loadPDFs()
  }, [clienteId, clienteIdCadastro]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="cliente-pdfs-modal">
      <div className="cliente-pdfs-content">
        <div className="cliente-pdfs-header">
          <h3>📄 PDFs do Cliente: {clienteNome}</h3>
          <div className="header-actions">
            {/* Upload só disponível quando clienteId existe (tabela clientes_pdfs usa clientes.id) */}
            {clienteId && (
              <button 
                onClick={() => setShowUploadForm(!showUploadForm)}
                className="btn btn-primary"
              >
                ➕ Adicionar PDF
              </button>
            )}
            <button onClick={onClose} className="btn btn-secondary">
              ✕ Fechar
            </button>
          </div>
        </div>

        {/* Formulário de Upload */}
        {showUploadForm && (
          <div className="upload-form">
            <h4>📤 Adicionar Novo PDF</h4>
            <form onSubmit={handleUpload}>
              <div className="form-group">
                <label htmlFor="tipo_pdf">Tipo do PDF: *</label>
                <select
                  id="tipo_pdf"
                  value={uploadData.tipo_pdf}
                  onChange={(e) => setUploadData({...uploadData, tipo_pdf: e.target.value})}
                  required
                >
                  <option value="vistoria">🔍 Vistoria</option>
                  <option value="projeto">📐 Projeto</option>
                  <option value="lista_material">📋 Lista de Material</option>
                  <option value="analise_sombreamento">☀️ Análise de Sombreamento</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="arquivo">Arquivo PDF: *</label>
                <input
                  type="file"
                  id="arquivo"
                  accept=".pdf"
                  onChange={(e) => setUploadData({...uploadData, arquivo: e.target.files[0]})}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="observacoes">Observações:</label>
                <textarea
                  id="observacoes"
                  value={uploadData.observacoes}
                  onChange={(e) => setUploadData({...uploadData, observacoes: e.target.value})}
                  placeholder="Observações sobre o PDF (opcional)"
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={uploading}
                >
                  {uploading ? '⏳ Enviando...' : '📤 Enviar PDF'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowUploadForm(false)}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Lista de PDFs */}
        <div className="pdfs-list">
          <h4>📚 PDFs Anexados ({pdfs.length})</h4>
          
          {loading ? (
            <div className="loading">⏳ Carregando PDFs...</div>
          ) : pdfs.length === 0 ? (
            <div className="no-pdfs">
              📄 Nenhum PDF anexado ainda.
            </div>
          ) : (
            <div className="pdfs-grid">
              {pdfs.map((pdf) => (
                <div key={pdf.id} className="pdf-card">
                  <div className="pdf-header">
                    <span className="pdf-icon">{getPDFIcon(pdf.tipo_pdf)}</span>
                    <span className="pdf-type">{getPDFTypeName(pdf.tipo_pdf)}</span>
                  </div>
                  
                  <div className="pdf-info">
                    <div className="pdf-name" title={pdf.nome_arquivo}>
                      {pdf.nome_arquivo}
                    </div>
                    <div className="pdf-details">
                      <span className="pdf-size">{formatFileSize(pdf.tamanho_arquivo)}</span>
                      <span className="pdf-date">{formatDate(pdf.data_upload)}</span>
                    </div>
                    {pdf.observacoes && (
                      <div className="pdf-observacoes" title={pdf.observacoes}>
                        💬 {pdf.observacoes}
                      </div>
                    )}
                  </div>

                  <div className="pdf-actions">
                    <button 
                      onClick={() => handleDownload(pdf)}
                      className="btn btn-sm btn-primary"
                      title="Baixar PDF"
                    >
                      ⬇️ Baixar
                    </button>
                    <button 
                      onClick={() => handleDelete(pdf.id)}
                      className="btn btn-sm btn-danger"
                      title="Excluir PDF"
                    >
                      🗑️ Excluir
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Componente de Notificação */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          duration={notification.duration}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Modal de Confirmação */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal">
            <div className="confirm-modal-header">
              <h3>⚠️ Confirmar Exclusão</h3>
            </div>
            <div className="confirm-modal-body">
              <p>Tem certeza que deseja excluir permanentemente este PDF?</p>
              <p><strong>⚠️ Esta ação não pode ser desfeita e removerá o arquivo do servidor!</strong></p>
              <p className="confirm-warning">Esta ação não pode ser desfeita.</p>
            </div>
            <div className="confirm-modal-actions">
              <button 
                onClick={handleCancel}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
              <button 
                onClick={handleConfirm}
                className="btn btn-danger"
              >
                🗑️ Excluir PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientePDFs
